import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAppContext } from './AppContext';

const FocusContext = createContext();

export function useFocusContext() {
  const context = useContext(FocusContext);
  if (!context) {
    throw new Error('useFocusContext must be used within FocusProvider');
  }
  return context;
}

export function FocusProvider({ children }) {
  const { settings } = useAppContext();
  
  // Timer state
  const [timeRemaining, setTimeRemaining] = useState(settings.timerDuration * 60); // in seconds
  const [isRunning, setIsRunning] = useState(false);
  const [timerMode, setTimerMode] = useState('focus'); // 'focus', 'shortBreak', 'longBreak'
  const [sessionCount, setSessionCount] = useState(0);

  // Handle timer completion
  const handleTimerComplete = useCallback(() => {
    setIsRunning(false);
    
    if (timerMode === 'focus') {
      setSessionCount(prev => prev + 1);
      // Optionally auto-start break
      // startBreak();
    }
    
    // Trigger notification (if enabled)
    if (settings.notifications && 'Notification' in window) {
      if (Notification.permission === 'granted') {
        new Notification('Timer Complete!', {
          body: timerMode === 'focus' ? 'Great work! Time for a break.' : 'Break over! Ready to focus?',
          icon: '/logo192.png'
        });
      }
    }
  }, [timerMode, settings.notifications]);

  // Timer interval
  useEffect(() => {
    let interval = null;

    if (isRunning && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining(prev => prev - 1);
      }, 1000);
    } else if (timeRemaining === 0) {
      // Timer completed
      handleTimerComplete();
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isRunning, timeRemaining, handleTimerComplete]);

  // Start timer
  const startTimer = () => {
    setIsRunning(true);
  };

  // Pause timer
  const pauseTimer = () => {
    setIsRunning(false);
  };

  // Reset timer
  const resetTimer = () => {
    setIsRunning(false);
    setTimeRemaining(getDurationForMode(timerMode));
  };

  // Get duration based on mode
  const getDurationForMode = (mode) => {
    switch (mode) {
      case 'focus':
        return settings.timerDuration * 60;
      case 'shortBreak':
        return settings.shortBreak * 60;
      case 'longBreak':
        return settings.longBreak * 60;
      default:
        return settings.timerDuration * 60;
    }
  };

  // Switch timer mode
  const switchMode = (newMode) => {
    setTimerMode(newMode);
    setTimeRemaining(getDurationForMode(newMode));
    setIsRunning(false);
  };

  // Start focus session
  const startFocus = () => {
    switchMode('focus');
  };

  // Start break
  const startBreak = (isLong = false) => {
    switchMode(isLong ? 'longBreak' : 'shortBreak');
  };

  // Format time for display (MM:SS)
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const value = {
    timeRemaining,
    isRunning,
    timerMode,
    sessionCount,
    startTimer,
    pauseTimer,
    resetTimer,
    startFocus,
    startBreak,
    switchMode,
    formatTime
  };

  return (
    <FocusContext.Provider value={value}>
      {children}
    </FocusContext.Provider>
  );
}

